import React, { forwardRef, useImperativeHandle, useRef, useMemo } from 'react';
import { DEFAULT_UNIT } from '../constants';
import { GenericSettingsPane, GenericSettingsPaneHandle } from './GenericSettingsPane';
import { PaneHandle, PaneProps } from '../accountParameter.types';
import { useStoreState } from '../../../../models/hooks';
import { useTranslation } from 'react-i18next';

export const InterviewsPane = forwardRef<PaneHandle, PaneProps>(({ onSave, setDirty, showActions }, ref) => {
  const genericPaneRef = useRef<GenericSettingsPaneHandle>(null);
  const account = useStoreState((state) => state.app.accounts);
  const { t } = useTranslation('accountParameter');

  useImperativeHandle(ref, () => ({
    save: () => genericPaneRef.current?.save(),
    reset: () => genericPaneRef.current?.reset(),
  }));

  const items = useMemo(
    () => [
      {
        key: 'freeOnHoldInterviews',
        label: t('interviewsPane.freeOnHoldInterviews'),
        description: t('interviewsPane.freeOnHoldInterviewsDescription'),
        value: account?.configuration?.freeOnHoldInterviews,
        type: 'boolean' as const,
      },
      {
        key: 'interviewRequestExpiryDays',
        label: t('interviewsPane.interviewRequestExpiryDays'),
        description: t('interviewsPane.interviewRequestExpiryDaysDescription'),
        value: account?.configuration?.interviewRequestExpiryDays,
        unit: DEFAULT_UNIT,
        min: 1,
        max: 28,
        type: 'number' as const,
        isRequired: true,
      },
      {
        key: 'appointmentHoldThresholdInDays',
        label: t('interviewsPane.appointmentHoldThresholdInDays'),
        description: t('interviewsPane.appointmentHoldThresholdInDaysDescription'),
        value: account?.configuration?.appointmentHoldThresholdInDays,
        unit: DEFAULT_UNIT,
        min: 1,
        max: 28,
        type: 'number' as const,
        isRequired: true,
      },
      {
        key: 'maxLimitInterviews',
        label: t('interviewsPane.maxLimitInterviews'),
        description: t('interviewsPane.maxLimitInterviewsDescription') + t('accountParametersView.positionOverWrittenWarning'),
        value: account?.configuration?.maxLimitInterviews ?? 50,
        unit: t('interviewsPane.count'),
        min: 0,
        max: 100,
        type: 'number' as const,
        isRequired: true,
      },
    ],
    [account, t],
  );

  return (
    <GenericSettingsPane ref={genericPaneRef} onSave={onSave} setDirty={setDirty} showInlineActions={showActions} items={items} />
  );
});
